// Thin wrapper hooks around RTK Query endpoints. Each hook adds:
//  - Memoised derived data (list, map, options) so consumers don't re-derive per render
//  - Skip logic when required IDs are missing (prevents wasted API calls)
//  - Consistent return shape: { ...query, list, map } (and options for dropdowns)
//
// Every reporting query is scoped by the currently selected organisation:
// `organisationId` is mandatory on the backend contract (see api.ts) so every
// hook here reads `useSelectedOrganisationId()` and forwards it. When the
// id isn't available yet (bootstrap window), the query is skipped — the
// empty-string fallback in the arg is purely to satisfy the required-string
// type and never reaches the network.
//
// These hooks live in @frontend/react (not the reporting app) so they can be
// shared with other apps that might need reporting data in the future.
import type {
  ReportingPlayer,
  ReportingSession,
  ReportingTeam
} from '@frontend/core/lib/domains/reporting'
import {
  useGetAllPlayersSessionSummaryQuery,
  useGetPlayerActionsQuery,
  useGetPlayerSessionSummaryQuery,
  useGetReportingTeamsQuery,
  useGetSessionPlayersQuery,
  useGetTeamPlayersQuery,
  useGetTeamSessionsQuery,
  useGetTeamSessionSummaryQuery
} from '@frontend/core/lib/domains/reporting'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { formatDateWithOrdinal } from '@frontend/utils'
import { useMemo } from 'react'
import { useImageUrl } from '../../modules/images/hooks'
import { useSelectedOrganisationId } from '../organisations/hooks'

export const useReportingTeams = () => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetReportingTeamsQuery(
    { organisationId: organisationId ?? '' },
    { skip: !organisationId }
  )
  const getImageUrl = useImageUrl()

  const list = useMemo(() => {
    return query.data ?? []
  }, [query.data])

  const map = useMemo(() => {
    return list.reduce(
      (acc, team) => {
        acc[team.id] = team

        return acc
      },
      {} as Record<string, ReportingTeam>
    )
  }, [list])

  const options = useMemo(() => {
    return list.map((team) => ({
      name: team.name,
      value: team.id,
      logo: getImageUrl(team.logo, IMAGE_PRESETS.teamLogo) || undefined
    }))
  }, [list, getImageUrl])

  return {
    ...query,
    list,
    map,
    options
  }
}

export const useReportingTeamSessions = (teamId: string) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetTeamSessionsQuery(
    { teamId, organisationId: organisationId ?? '' },
    { skip: !teamId || !organisationId }
  )

  // Reverse chronological (most recent first) — backend returns ascending.
  const list = useMemo(() => {
    const data = query.data ?? []

    return [...data].sort((a, b) => b.date.localeCompare(a.date))
  }, [query.data])

  const map = useMemo(() => {
    return list.reduce(
      (acc, session) => {
        acc[session.id] = session

        return acc
      },
      {} as Record<string, ReportingSession>
    )
  }, [list])

  const options = useMemo(() => {
    return list.map((session) => {
      const formattedDate = formatDateWithOrdinal(session.date)
      const name = formattedDate
        ? `${formattedDate} - ${session.name}`
        : session.name

      return { name, value: session.id }
    })
  }, [list])

  return {
    ...query,
    list,
    map,
    options
  }
}

export const useReportingTeamPlayers = (teamId: string) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetTeamPlayersQuery(
    { teamId, organisationId: organisationId ?? '' },
    { skip: !teamId || !organisationId }
  )

  const list = useMemo(() => {
    return query.data ?? []
  }, [query.data])

  const map = useMemo(() => {
    return list.reduce(
      (acc, player) => {
        acc[player.id] = player

        return acc
      },
      {} as Record<string, ReportingPlayer>
    )
  }, [list])

  const options = useMemo(() => {
    return list.map((player) => ({
      name: player.displayName || `${player.firstName} ${player.lastName}`,
      value: player.id,
      number: player.number
    }))
  }, [list])

  return {
    ...query,
    list,
    map,
    options
  }
}

export const useSessionPlayers = (teamId: string, sessionId: string) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetSessionPlayersQuery(
    { teamId, sessionId, organisationId: organisationId ?? '' },
    { skip: !teamId || !sessionId || !organisationId }
  )

  const list = useMemo(() => query.data ?? [], [query.data])

  const map = useMemo(
    () =>
      list.reduce(
        (acc, player) => {
          acc[player.id] = player

          return acc
        },
        {} as Record<string, ReportingPlayer>
      ),
    [list]
  )

  return {
    ...query,
    list,
    map
  }
}

export const useTeamSessionSummary = (teamId: string, sessionId: string) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetTeamSessionSummaryQuery(
    { teamId, sessionId, organisationId: organisationId ?? '' },
    { skip: !teamId || !sessionId || !organisationId }
  )

  return query
}

export const useAllPlayersSessionSummary = (
  teamId: string,
  sessionId: string,
  periodId?: string,
  position?: string
) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetAllPlayersSessionSummaryQuery(
    {
      teamId,
      sessionId,
      organisationId: organisationId ?? '',
      periodId: periodId || undefined,
      position: position || undefined
    },
    {
      skip: !teamId || !sessionId || !organisationId
    }
  )

  const playerStats = useMemo(() => {
    return query.data?.playerStats ?? []
  }, [query.data?.playerStats])

  const totalStats = useMemo(() => {
    return query.data?.totalStats ?? null
  }, [query.data?.totalStats])

  const map = useMemo(() => {
    return playerStats.reduce(
      (acc, entry) => {
        acc[entry.playerId] = entry.stats

        return acc
      },
      {} as Record<string, (typeof playerStats)[number]['stats']>
    )
  }, [playerStats])

  return {
    ...query,
    playerStats,
    totalStats,
    map
  }
}

export const usePlayerSessionSummary = (
  teamId: string,
  sessionId: string,
  playerId: string
) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetPlayerSessionSummaryQuery(
    {
      teamId,
      sessionId,
      playerId,
      organisationId: organisationId ?? ''
    },
    {
      skip: !teamId || !sessionId || !playerId || !organisationId
    }
  )

  return query
}

export const usePlayerActions = (
  teamId: string,
  sessionId: string,
  playerId: string
) => {
  const organisationId = useSelectedOrganisationId()
  const query = useGetPlayerActionsQuery(
    {
      teamId,
      sessionId,
      playerId,
      organisationId: organisationId ?? ''
    },
    {
      skip: !teamId || !sessionId || !playerId || !organisationId
    }
  )

  const list = useMemo(() => {
    return query.data?.actions ?? []
  }, [query.data?.actions])

  return {
    ...query,
    list
  }
}
